import { Spinner } from '@chakra-ui/react'
import React, { useEffect } from 'react'
import { i18n } from '@lingui/core'
import { camelCase, kebabCase } from 'lodash'

import { useControlledSelectFromEnumWithUrlQuery } from 'front-end/src/app/hooks/useControlledSelectFromEnum'
import usePromise from 'front-end/src/app/hooks/usePromise'
import {
  CustomTranslationsSectionEnum,
  SupportedLocalesEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { loadLocaleMessages } from 'front-end/src/i18n/i18n'

import { useCustomTranslationsTabQuery } from './CustomTranslationsTab.codegen'
import { CustomTranslationTabContent } from './CustomTranslationTabContent'
import { forbiddenQuestionsTranslations } from '../../auth/security-questions/SecurityQuestionsOptions'

const getTranslation = async (lang) => {
  const translations = await import(`../../../locale/${lang}/messages.ts`)
  return {
    ...translations,
    messages: Object.fromEntries(
      Object.entries(translations.messages).filter(
        ([t]) => !forbiddenQuestionsTranslations.has(t)
      )
    )
  }
}

export const CustomTranslationsTab: React.FC<{}> = () => {
  const kebabCaseLocale = i18n.locale ?? SupportedLocalesEnum.enGb

  const currentLocale = camelCase(kebabCaseLocale) as SupportedLocalesEnum

  const { controlledSelect: localeSelect, selected: selectedLocale } =
    useControlledSelectFromEnumWithUrlQuery({
      enu: SupportedLocalesEnum,
      queryParamName: 'lang',
      defaultValue: currentLocale,
      omit: [SupportedLocalesEnum.pseudo]
    })

  const { controlledSelect: appSectionSelect, selected: selectedSection } =
    useControlledSelectFromEnumWithUrlQuery({
      enu: CustomTranslationsSectionEnum,
      queryParamName: 'section',
      defaultValue: CustomTranslationsSectionEnum.FRONTEND_UI
    })

  useEffect(() => {
    return () => {
      loadLocaleMessages({ locale: currentLocale }) // we always reinitialize i18n when user leaves this page to show the changes without having to refresh browser
    }
  }, [])

  const { data, loading } = useCustomTranslationsTabQuery({
    variables: {
      locale: selectedLocale as SupportedLocalesEnum,
      appSection: selectedSection as CustomTranslationsSectionEnum
    }
  })

  const { value: bundledLocalisation } = usePromise(
    () => getTranslation(kebabCase(selectedLocale)),
    [selectedLocale]
  )
  if (!bundledLocalisation || loading) {
    return <Spinner />
  }
  return (
    <CustomTranslationTabContent
      translations={data}
      selectedLocale={selectedLocale as SupportedLocalesEnum}
      selectedSection={selectedSection as CustomTranslationsSectionEnum}
      bundledLocalisation={bundledLocalisation}
      localeSelect={localeSelect}
      appSectionSelect={appSectionSelect}
    />
  )
}
